'use client';

import { useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselControls,
  CarouselNavigation,
  CarouselDots,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import type { QuoteItem, QuoteCardVariant } from '@/app/_components/shared/quote-section/quote-section.types';
import { useLabel } from '@/app/_contexts/labels-context';
import { useResponsiveSlidesPerView } from '@/app/_hooks/use-responsive-slides-per-view';
import { QuoteCard } from './quote-card';

interface QuoteCarouselProps {
  quotes: QuoteItem[];
  variant?: QuoteCardVariant;
  showNavigation: boolean;
  showDots: boolean;
  autoplay: boolean;
}

export function QuoteCarousel({
  quotes,
  variant = 'avatar',
  showNavigation,
  showDots,
  autoplay,
}: QuoteCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const currentSlidesPerView = useResponsiveSlidesPerView(3);
  const canScroll = quotes.length > currentSlidesPerView;
  const carouselLabel = useLabel('carousel.quotes', 'Quotes');

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: 'start',
        loop: autoplay && canScroll,
        watchDrag: canScroll,
      }}
      autoplay={autoplay && canScroll}
      ariaLabel={carouselLabel}
    >
      <CarouselContent className="-ml-6">
        {quotes.map((item, index) => (
          <CarouselItem
            key={item.id ?? `${item.name}-${index}`}
            className="basis-full pl-6 md:basis-1/2 lg:basis-1/3"
          >
            <QuoteCard item={item} variant={variant} />
          </CarouselItem>
        ))}
      </CarouselContent>

      {(showNavigation || showDots) && canScroll && (
        <CarouselControls className="mt-6">
          {showNavigation && <CarouselNavigation api={api} />}
          {showDots && <CarouselDots api={api} />}
        </CarouselControls>
      )}
    </Carousel>
  );
}
